import React from 'react';
import { Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import AdminUserRow from '../AdminUserRow';
import { ROLE_COLORS, ROLE_NAMES } from '../adminConstants';

/**
 * Users tab body for the Admin Dashboard.
 *
 * Extracted from AdminDashboardPage.jsx so the page's render tree stays
 * scannable. All state lives in the parent — this component is a pure
 * view of the props it receives.
 */
export default function UsersTab({
  users,
  filteredUsers,
  organizations,
  currentUser,
  authIsSuperAdmin,
  searchTerm,
  setSearchTerm,
  roleFilter,
  setRoleFilter,
  selectedOrg,
  setSelectedOrg,
  setViewingUser,
  openEditUser,
  setChangeRoleUser,
  setNewRoleValue,
  handleResendInvitation,
  handleDeleteUser,
  startImpersonation,
  navigate,
  pulsingUserId,
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>Manage user accounts and permissions</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <Input
                placeholder="Search users..."
                className="pl-9 w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="admin-users-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-36" data-testid="admin-users-role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="org_admin">Org Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
            {currentUser?.is_super_admin && (
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger className="w-48" data-testid="admin-users-org-filter">
                  <SelectValue placeholder="Filter by org" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {filteredUsers.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No users found</p>
          ) : (
            filteredUsers.map((user) => {
              const primaryRole = user.roles?.[0]?.role || 'user';
              const roleConfig = ROLE_COLORS[primaryRole] || ROLE_COLORS.user;
              const canImpersonate =
                authIsSuperAdmin && user.user_id !== currentUser?.user_id;
              return (
                <AdminUserRow
                  key={user.user_id}
                  user={user}
                  primaryRole={primaryRole}
                  roleConfig={roleConfig}
                  roleName={ROLE_NAMES[primaryRole]}
                  isPulsing={pulsingUserId === user.user_id}
                  onView={(u) => setViewingUser(u)}
                  onEdit={openEditUser}
                  onChangeRole={(u) => {
                    setChangeRoleUser(u);
                    setNewRoleValue(u.roles?.[0]?.role || 'user');
                  }}
                  onResendInvite={handleResendInvitation}
                  onDelete={(u) => handleDeleteUser(u.user_id, u.email)}
                  canImpersonate={canImpersonate}
                  onImpersonate={(u) => {
                    const orgId = u.roles?.[0]?.organization_id;
                    const orgName =
                      organizations.find((o) => o.id === orgId)?.name || orgId;
                    startImpersonation({
                      ...u,
                      organization_id: orgId,
                      organization_name: orgName,
                    });
                    navigate('/');
                  }}
                />
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
